// ============================================
// MEDFLOW - Settings Page
// ============================================

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2, Key, Bell, User, Copy, Trash2, Plus, Check } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { apiKeysApi } from '@/lib/api';
import { isSupabaseAuthEnabled } from '@/lib/auth-config';
import { setSupabaseDefaultClinic, updateSupabasePassword } from '@/lib/supabase-auth';
import { useAuthStore } from '@/stores/auth';
import { cn, formatDate } from '@/lib/utils';

interface ApiKey {
  id: string;
  name: string;
  createdAt: string;
  lastUsedAt?: string;
  isActive: boolean;
}

const TABS = [
  { id: 'clinic', label: 'Clinica', icon: Building2 },
  { id: 'api-keys', label: 'API Keys', icon: Key },
  { id: 'notifications', label: 'Notificaciones', icon: Bell },
  { id: 'profile', label: 'Perfil', icon: User },
] as const;

type TabId = typeof TABS[number]['id'];

export function SettingsPage() {
  const [activeTab, setActiveTab] = useState<TabId>('clinic');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Configuracion</h1>
        <p className="text-gray-600">Administra la configuracion de tu clinica</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <nav className="lg:w-64 flex-shrink-0">
          <Card>
            <CardContent className="p-2">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors',
                    activeTab === tab.id ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-100'
                  )}
                >
                  <tab.icon className="w-5 h-5" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              ))}
            </CardContent>
          </Card>
        </nav>

        <div className="flex-1">
          {activeTab === 'clinic' && <ClinicSettings />}
          {activeTab === 'api-keys' && <ApiKeysSettings />}
          {activeTab === 'notifications' && <NotificationSettings />}
          {activeTab === 'profile' && <ProfileSettings />}
        </div>
      </div>
    </div>
  );
}

function ClinicSettings() {
  const { clinic, memberships, token, setSupabaseAuth } = useAuthStore();
  const queryClient = useQueryClient();

  const switchClinicMutation = useMutation({
    mutationFn: (clinicId: string) => setSupabaseDefaultClinic(clinicId),
    onSuccess: (context) => {
      if (!context.user) {
        return;
      }

      setSupabaseAuth(
        {
          user: {
            id: context.user.id,
            email: context.user.email ?? '',
            name: context.user.name,
            phone: context.user.phone ?? undefined,
            defaultClinicId: context.user.defaultClinicId ?? null,
          },
          clinic: context.clinic
            ? {
                id: context.clinic.id,
                name: context.clinic.name,
                slug: context.clinic.slug,
                timezone: context.clinic.timezone,
                phone: context.clinic.phone ?? null,
                email: context.clinic.email ?? null,
                isActive: context.clinic.isActive,
              }
            : null,
          memberships: context.memberships.map((membership) => ({
            id: membership.id,
            clinicId: membership.clinicId,
            role: membership.role,
            status: membership.status,
            permissions: membership.permissions,
            clinic: {
              id: membership.clinic.id,
              name: membership.clinic.name,
              slug: membership.clinic.slug,
              timezone: membership.clinic.timezone,
              phone: membership.clinic.phone ?? null,
              email: membership.clinic.email ?? null,
              isActive: membership.clinic.isActive,
            },
          })),
        },
        token
      );

      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      queryClient.invalidateQueries({ queryKey: ['patients'] });
      queryClient.invalidateQueries({ queryKey: ['professionals'] });
      queryClient.invalidateQueries({ queryKey: ['services'] });
      queryClient.invalidateQueries({ queryKey: ['boxes'] });
      queryClient.invalidateQueries({ queryKey: ['finance'] });
    },
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Informacion de la Clinica</CardTitle>
          <CardDescription>Configura los datos generales de tu clinica</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input label="Nombre de la clinica" defaultValue={clinic?.name || ''} placeholder="Clinica Dental ABC" />
          <Input label="Direccion" placeholder="Av. Principal 123" />
          <div className="grid gap-4 md:grid-cols-2">
            <Input label="Telefono" defaultValue={clinic?.phone || ''} placeholder="+56 9 1234 5678" />
            <Input label="Email de contacto" type="email" defaultValue={clinic?.email || ''} placeholder="contacto@clinica.com" />
          </div>
          <div className="pt-4">
            <Button>Guardar cambios</Button>
          </div>
        </CardContent>
      </Card>

      {isSupabaseAuthEnabled && (
        <Card>
          <CardHeader>
            <CardTitle>Espacios y membresias</CardTitle>
            <CardDescription>Administra la clinica activa dentro del nuevo workspace multi-tenant</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {memberships.length === 0 ? (
              <p className="text-sm text-gray-500">Aun no tienes membresias activas en Supabase.</p>
            ) : (
              memberships.map((membership) => {
                const isActiveClinic = membership.clinic.id === clinic?.id;

                return (
                  <div key={membership.id} className="flex flex-col gap-3 rounded-lg border p-4 md:flex-row md:items-center md:justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900">{membership.clinic.name}</p>
                        {isActiveClinic && (
                          <Badge className="bg-primary/10 text-primary">Activa</Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">Rol: {membership.role} · Slug: {membership.clinic.slug}</p>
                    </div>

                    <Button
                      variant={isActiveClinic ? 'secondary' : 'outline'}
                      disabled={isActiveClinic || switchClinicMutation.isPending}
                      loading={switchClinicMutation.isPending && !isActiveClinic}
                      onClick={() => switchClinicMutation.mutate(membership.clinic.id)}
                    >
                      {isActiveClinic ? 'Clinica activa' : 'Usar esta clinica'}
                    </Button>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ApiKeysSettings() {
  const [showNewKey, setShowNewKey] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['api-keys'],
    queryFn: () => apiKeysApi.list(),
  });

  const apiKeys = (data?.data as ApiKey[]) || [];

  const createMutation = useMutation({
    mutationFn: (name: string) => apiKeysApi.create(name),
    onSuccess: (response: { data?: { key: string } }) => {
      setCreatedKey(response.data?.key || '');
      setNewKeyName('');
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => apiKeysApi.revoke(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    },
  });

  const handleCopy = async (key: string, id: string) => {
    await navigator.clipboard.writeText(key);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleCreate = () => {
    if (newKeyName.trim()) {
      createMutation.mutate(newKeyName.trim());
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>API Keys</CardTitle>
          <CardDescription>Crea claves de API para integrar con n8n u otras herramientas externas</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {showNewKey ? (
            <div className="p-4 border rounded-lg bg-gray-50 space-y-4">
              <Input label="Nombre de la clave" value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)} placeholder="ej: n8n-produccion" />
              <div className="flex gap-2">
                <Button onClick={handleCreate} disabled={!newKeyName.trim() || createMutation.isPending} loading={createMutation.isPending}>
                  Crear clave
                </Button>
                <Button variant="outline" onClick={() => { setShowNewKey(false); setNewKeyName(''); }}>
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            <Button onClick={() => setShowNewKey(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Nueva API Key
            </Button>
          )}

          {createdKey && (
            <div className="p-4 border border-green-200 rounded-lg bg-green-50">
              <p className="text-sm text-green-800 mb-2">API Key creada exitosamente! Copia esta clave ahora, no la veras de nuevo.</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 p-2 bg-white rounded border font-mono text-sm">{createdKey}</code>
                <Button variant="outline" size="icon" onClick={() => handleCopy(createdKey, 'new')}>
                  {copiedId === 'new' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <Button variant="ghost" size="sm" className="mt-2" onClick={() => setCreatedKey(null)}>
                Entendido, ya la copie
              </Button>
            </div>
          )}

          {isLoading ? (
            <div className="h-32 bg-gray-100 rounded animate-pulse" />
          ) : apiKeys.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No tienes API Keys creadas</div>
          ) : (
            <div className="divide-y">
              {apiKeys.map((key) => (
                <div key={key.id} className="py-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium">{key.name}</p>
                    <p className="text-sm text-gray-500">
                      Creada: {formatDate(key.createdAt)}
                      {key.lastUsedAt && <> - Ultimo uso: {formatDate(key.lastUsedAt)}</>}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={key.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                      {key.isActive ? 'Activa' : 'Revocada'}
                    </Badge>
                    {key.isActive && (
                      <Button variant="outline" size="sm" onClick={() => revokeMutation.mutate(key.id)} disabled={revokeMutation.isPending}>
                        <Trash2 className="w-4 h-4 mr-1" />
                        Revocar
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Integracion con n8n</CardTitle>
          <CardDescription>Como usar tu API Key en n8n para conectar con Medflow</CardDescription>
        </CardHeader>
        <CardContent className="prose prose-sm max-w-none">
          <ol className="list-decimal list-inside space-y-2 text-gray-600">
            <li>Crea una API Key con un nombre descriptivo (ej: n8n-produccion)</li>
            <li>En n8n, usa el nodo HTTP Request</li>
            <li>Configura la autenticacion como Header Auth</li>
            <li>Nombre del header: <code className="px-1 py-0.5 bg-gray-100 rounded">X-API-Key</code></li>
            <li>Valor: tu API Key creada (comienza con <code className="px-1 py-0.5 bg-gray-100 rounded">mf_</code>)</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}

function NotificationSettings() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Notificaciones</CardTitle>
        <CardDescription>Configura como recibir notificaciones</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium">Recordatorios de citas</p>
            <p className="text-sm text-gray-500">Enviar recordatorio automatico a pacientes</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" className="sr-only peer" defaultChecked />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
          </label>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium">Notificacion de nuevas citas</p>
            <p className="text-sm text-gray-500">Recibir email cuando se agende una nueva cita</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" className="sr-only peer" defaultChecked />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
          </label>
        </div>

        <div className="pt-4">
          <Button>Guardar preferencias</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ProfileSettings() {
  const { user } = useAuthStore();
  const [passwordState, setPasswordState] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null);

  const passwordMutation = useMutation({
    mutationFn: async () => {
      if (!isSupabaseAuthEnabled) {
        throw new Error('El cambio de contraseña via UI aún está pendiente para el modo legacy.');
      }

      if (passwordState.newPassword.length < 6) {
        throw new Error('La nueva contraseña debe tener al menos 6 caracteres.');
      }

      if (passwordState.newPassword !== passwordState.confirmPassword) {
        throw new Error('Las contraseñas no coinciden.');
      }

      await updateSupabasePassword(passwordState.newPassword);
    },
    onSuccess: () => {
      setPasswordMessage('Contraseña actualizada correctamente.');
      setPasswordState({ currentPassword: '', newPassword: '', confirmPassword: '' });
    },
    onError: (error: Error) => {
      setPasswordMessage(error.message);
    },
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Mi Perfil</CardTitle>
          <CardDescription>Actualiza tu informacion personal</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input label="Nombre completo" defaultValue={user?.name || ''} placeholder="Juan Perez" />
          <Input label="Email" type="email" defaultValue={user?.email || ''} placeholder="juan@clinica.com" />
          <Input label="Telefono" defaultValue={user?.phone || ''} placeholder="+56 9 1234 5678" />
          <div className="pt-4">
            <Button>Guardar cambios</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Cambiar Contrasena</CardTitle>
          <CardDescription>Actualiza tu contrasena de acceso</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {passwordMessage && (
            <div className={cn(
              'rounded-lg p-3 text-sm',
              passwordMutation.isError ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
            )}>
              {passwordMessage}
            </div>
          )}

          <Input
            label="Contrasena actual"
            type="password"
            placeholder="........"
            disabled={isSupabaseAuthEnabled}
            value={passwordState.currentPassword}
            onChange={(event) => setPasswordState((current) => ({ ...current, currentPassword: event.target.value }))}
          />
          <Input
            label="Nueva contrasena"
            type="password"
            placeholder="........"
            value={passwordState.newPassword}
            onChange={(event) => setPasswordState((current) => ({ ...current, newPassword: event.target.value }))}
          />
          <Input
            label="Confirmar nueva contrasena"
            type="password"
            placeholder="........"
            value={passwordState.confirmPassword}
            onChange={(event) => setPasswordState((current) => ({ ...current, confirmPassword: event.target.value }))}
          />
          <div className="pt-4">
            <Button onClick={() => passwordMutation.mutate()} loading={passwordMutation.isPending}>
              Cambiar contrasena
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
